import { useMutation, useQueries, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import { PageHeader } from "../components/AppShell.js";
import { Button, Notice } from "../components/ui.js";
import { fetchPoolState, isPaused, startAuction } from "../lib/auction.js";
import { hasRole, useAuth } from "../lib/auth.js";
import { fetchFranchises } from "../lib/franchises.js";
import { lotLabel, lotWarnings } from "../lib/lot-warnings.js";
import { fetchPoolLots } from "../lib/lots.js";
import { fetchTournaments } from "../lib/tournaments.js";
import type { Tournament } from "../lib/types.js";
import { auctionStateKey } from "./auction/parts.js";

/** How often the console asks again while the auction runs. A second organiser's taps show up. */
const CONSOLE_REFRESH_MS = 3000;

/**
 * The auction's hub: Start, then a card per pool. Each pool has a screen of its own, where an
 * auctioned pool is called player by player and an assigned one is placed by hand.
 *
 * Organisers only, before, during and after. The database refuses everybody else every call
 * and sends them no state.
 */
export function TournamentAuction() {
  const { tournamentId } = useParams<{ tournamentId: string }>();
  const { member } = useAuth();
  const {
    data: tournaments,
    isPending,
    error,
  } = useQuery({
    queryKey: ["tournaments"],
    queryFn: fetchTournaments,
  });
  const tournament = tournaments?.find((candidate) => candidate.id === tournamentId);

  if (!hasRole(member, "organiser")) {
    return <Navigate to={`/tournaments/${tournamentId}`} replace />;
  }

  if (isPending) {
    return (
      <main className="mx-auto max-w-3xl px-4 pb-16 pt-6">
        <p className="micro">Loading…</p>
      </main>
    );
  }

  if (error || !tournament) {
    return (
      <main className="mx-auto max-w-3xl px-4 pb-16 pt-6">
        <Link to="/events" className="micro mb-4 inline-block underline">
          ← All events
        </Link>
        <Notice tone="attention">
          {error
            ? "Couldn't load this tournament. Try again."
            : "That tournament isn't here any more."}
        </Notice>
      </main>
    );
  }

  if (!tournament.auction) {
    return <Navigate to={`/tournaments/${tournament.id}/manage`} replace />;
  }

  const started = tournament.auctionStartedAt !== null;

  return (
    <main className="mx-auto max-w-3xl px-4 pb-16 pt-6">
      <Link
        to={`/tournaments/${tournament.id}/manage`}
        className="micro mb-4 inline-block underline"
      >
        ← {tournament.name}
      </Link>
      <PageHeader eyebrow={`Organise · ${tournament.name}`} title="Auction" />
      {started ? <Pools tournament={tournament} /> : <StartPanel tournament={tournament} />}
    </main>
  );
}

// ---------------------------------------------------------------------------
// Before the start
// ---------------------------------------------------------------------------

/**
 * What stands in the way of Start, pool by pool, and Start itself. Three things block: lots not
 * published, a player in no lot, a lot without a base price. An uneven lot and a short purse
 * are said, and do not. Assigned pools need nothing.
 */
function StartPanel({ tournament }: { tournament: Tournament }) {
  const queryClient = useQueryClient();
  const [confirming, setConfirming] = useState(false);
  const auctioned = tournament.events.filter((event) => event.squadFill === "auction");
  const assigned = tournament.events.filter((event) => event.squadFill === "assigned");

  const { data: setup } = useQuery({
    queryKey: ["franchises", tournament.id],
    queryFn: () => fetchFranchises(tournament.id),
  });
  const pools = useQueries({
    queries: auctioned.map((event) => ({
      queryKey: ["pool-lots", event.id],
      queryFn: () => fetchPoolLots(event.id, event.entries),
    })),
  });

  const start = useMutation({
    mutationFn: () => startAuction(tournament.id),
    onSuccess: () => {
      setConfirming(false);
      void queryClient.invalidateQueries({ queryKey: ["tournaments"] });
    },
  });

  const names = new Map((setup?.franchises ?? []).map((f) => [f.id, f.name]));
  const readiness = auctioned.map((event, i) => {
    const data = pools[i]?.data;
    const blockers: string[] = [];
    if (event.lotsPublishedAt === null) blockers.push("Lots are not published");
    if (data && data.unassigned.length > 0) {
      blockers.push(
        `${data.unassigned.length} ${data.unassigned.length === 1 ? "player is" : "players are"} in no lot`,
      );
    }
    data?.lots.forEach((lot, index) => {
      if (lot.basePrice === null) blockers.push(`${lotLabel(lot, index)} has no base price`);
    });
    const squads = (setup?.squads ?? []).filter((squad) => squad.eventId === event.id);
    const warnings = data
      ? lotWarnings({
          lots: data.lots,
          unassigned: 0,
          teams: squads.length,
          currency: tournament.auctionCurrency,
          purses: squads.flatMap((squad) =>
            squad.purse === null
              ? []
              : [
                  {
                    squadId: squad.id,
                    name: names.get(squad.franchiseId) ?? "A team",
                    purse: squad.purse,
                  },
                ],
          ),
        }).filter((w) => w.kind === "uneven" || w.kind === "purse")
      : [];
    return { event, blockers, warnings, loading: !data };
  });

  const blocked = readiness.some((r) => r.loading || r.blockers.length > 0);
  const noFranchises = setup !== undefined && setup.franchises.length === 0;

  return (
    <>
      <p className="mb-6 text-sm text-ink-muted">
        Starting fixes the franchises, captains, purses, seeding, lots and rules. Then you run each
        auctioned pool in turn, and place the players of the others by hand.
      </p>
      <ul className="mb-6">
        {readiness.map(({ event, blockers, warnings, loading }) => (
          <li
            key={event.id}
            className="border-b py-3"
            style={{ borderColor: "var(--color-line-muted)" }}
          >
            <p className="text-base font-bold">{event.name}</p>
            {loading ? (
              <p className="micro">Checking…</p>
            ) : blockers.length === 0 ? (
              <p className="font-mono text-[11px] text-ink-muted">Ready to auction</p>
            ) : null}
            {blockers.map((b) => (
              <p key={b} className="text-sm" style={{ color: "var(--color-attention)" }}>
                {b}
              </p>
            ))}
            {warnings.map((w) => (
              <p key={w.message} className="text-sm text-ink-muted">
                {w.message}
              </p>
            ))}
          </li>
        ))}
        {assigned.map((event) => (
          <li
            key={event.id}
            className="border-b py-3"
            style={{ borderColor: "var(--color-line-muted)" }}
          >
            <p className="text-base font-bold">{event.name}</p>
            <p className="font-mono text-[11px] text-ink-muted">
              Players placed by you after the start
            </p>
          </li>
        ))}
      </ul>
      {noFranchises ? <Notice tone="attention">Add franchises before starting.</Notice> : null}
      {start.error ? <Notice tone="attention">{start.error.message}</Notice> : null}
      {confirming ? (
        <div className="flex flex-wrap items-center gap-3">
          <p className="w-full text-sm">
            Start the auction? The setup can&rsquo;t change after this.
          </p>
          <Button tone="primary" disabled={start.isPending} onClick={() => start.mutate()}>
            {start.isPending ? "Starting…" : "Yes, start"}
          </Button>
          <Button tone="quiet" onClick={() => setConfirming(false)}>
            Not yet
          </Button>
        </div>
      ) : (
        <Button
          tone="primary"
          disabled={blocked || noFranchises}
          onClick={() => setConfirming(true)}
        >
          Start the auction
        </Button>
      )}
    </>
  );
}

// ---------------------------------------------------------------------------
// After the start
// ---------------------------------------------------------------------------

/**
 * One card per pool, each a way into its own screen: whether it is auctioned or placed, where it
 * stands, and how many of its players have a team. The open pool is the live one, on the
 * highlighter.
 */
function Pools({ tournament }: { tournament: Tournament }) {
  const states = useQueries({
    queries: tournament.events.map((event) => ({
      queryKey: auctionStateKey(event.id),
      queryFn: () => fetchPoolState(event.id),
      refetchInterval: event.auctionFinishedAt === null ? CONSOLE_REFRESH_MS : (false as const),
    })),
  });

  return (
    <>
      <p className="mb-6 text-sm text-ink-muted">
        Run the auctioned pools one at a time, in any order, and place the others by hand.{" "}
        <Link to={`/tournaments/${tournament.id}/auction/board`} className="underline">
          Open the board
        </Link>{" "}
        on a screen the room can see; it follows whichever pool is open.
      </p>
      <ul className="grid gap-3 sm:grid-cols-2">
        {tournament.events.map((event, i) => {
          const state = states[i]?.data;
          const total = state?.players.length ?? 0;
          const placed = state?.players.filter((p) => p.squadId !== null).length ?? 0;
          const status = !state
            ? "…"
            : state.finished
              ? "Finished"
              : state.open
                ? "Open"
                : isPaused(state)
                  ? "Paused"
                  : state.fill === "assigned"
                    ? placed > 0
                      ? "Placing"
                      : "To place"
                    : "Waiting";
          return (
            <li key={event.id}>
              <Link
                to={`/tournaments/${tournament.id}/auction/${event.id}`}
                aria-label={`${event.name}, ${status}`}
                className="flex h-full flex-col gap-3 border-2 p-4"
                style={{
                  borderColor: state?.open ? "var(--color-line)" : "var(--color-line-muted)",
                }}
              >
                <span className="flex items-start justify-between gap-3">
                  <span className="text-lg font-extrabold leading-tight">{event.name}</span>
                  <span
                    className={`font-mono shrink-0 px-2 py-0.5 text-[11px] font-semibold uppercase tracking-[0.1em] ${
                      state?.open ? "highlight" : ""
                    }`}
                    style={
                      state?.open ? undefined : { border: "1px solid var(--color-line-muted)" }
                    }
                  >
                    {status}
                  </span>
                </span>
                <span className="micro">
                  {event.squadFill === "assigned" ? "Placed by you" : "Auction"}
                  {state && state.lots.length > 0 ? ` · ${state.lots.length} lots` : ""}
                </span>
                <span className="mt-auto">
                  <span
                    className="block h-1.5"
                    style={{ background: "var(--color-line-muted)" }}
                    aria-hidden
                  >
                    <span
                      className="block h-full"
                      style={{
                        width: `${total ? (placed / total) * 100 : 0}%`,
                        background: "var(--color-line)",
                      }}
                    />
                  </span>
                  <span className="mt-1 block font-mono text-[11px]">
                    {placed} of {total} in a team
                  </span>
                </span>
              </Link>
            </li>
          );
        })}
      </ul>
    </>
  );
}

/** The way in from the Organise page. */
export function AuctionRow({ tournament }: { tournament: Tournament }) {
  const finished = tournament.events.filter((e) => e.auctionFinishedAt !== null).length;
  const facts =
    tournament.auctionStartedAt === null
      ? "Not started"
      : `Started · ${finished} of ${tournament.events.length} ${tournament.events.length === 1 ? "pool" : "pools"} finished`;
  return (
    <ul>
      <AuctionRowLink
        to={`/tournaments/${tournament.id}/auction`}
        title="Run the auction"
        facts={facts}
      />
      {/* The room's screen: worth putting up on the television before the first pool opens. */}
      <AuctionRowLink
        to={`/tournaments/${tournament.id}/auction/board`}
        title="Open the board"
        facts="For a screen the room can see · follows the open pool"
      />
    </ul>
  );
}

function AuctionRowLink({ to, title, facts }: { to: string; title: string; facts: string }) {
  return (
    <li className="border-b" style={{ borderColor: "var(--color-line-muted)" }}>
      <Link to={to} className="flex min-h-[56px] items-center gap-3 py-2">
        <span className="flex min-w-0 flex-1 flex-col gap-1">
          <span className="text-base font-bold">{title}</span>
          <span className="font-mono text-[11px] text-ink-muted">{facts}</span>
        </span>
        <svg
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.2"
          aria-hidden
          className="shrink-0"
        >
          <path d="M9 5l7 7-7 7" />
        </svg>
      </Link>
    </li>
  );
}
